import axios from 'axios';
import { API_BASE_URL, configureOrdinaryApi } from '../../../security/ordinarySession';

// Базовый URL для API Gateway

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

configureOrdinaryApi(api);

// Типы для API
export interface Term {
  id: string;
  term_arabic: string;
  term_transliteration: string;
  term_transliteration_cyr: string;
  category: string;
  definition_short: string;
  definition_full: string;
  keywords: string[];
  difficulty_level: number;
  importance: number;
  created_at: string;
}

export interface FavoriteTerm extends Term {
  created_at: string;
}

export interface ViewHistoryTerm extends Term {
  view_count: number;
  last_viewed_at: string;
  created_at: string;
}

export interface QuizSession {
  id: string;
  user_id: string;
  category?: string;
  difficulty?: string;
  total_questions: number;
  current_question: number;
  score: number;
  status: string;
  time_limit?: number;
  created_at: string;
  completed_at?: string;
}

export interface QuizAnswer {
  id: string;
  session_id: string;
  question_id: string;
  term_id: string;
  question_type: string;
  question_text: string;
  user_answer?: string;
  correct_answer: string;
  is_correct: boolean;
  time_spent?: number;
  confidence?: number;
  created_at: string;
}

export interface UserStats {
  favorites_count: number;
  view_stats: {
    total_views: number;
    unique_terms: number;
    average_views_per_term: number;
  };
}

export interface QuizStats {
  total_sessions: number;
  completed_sessions: number;
  total_score: number;
  total_questions: number;
  correct_answers: number;
  total_answers: number;
  accuracy_percentage: number;
  average_score: number;
}

// API для работы с терминами
export const arabicTermsApi = {
  // Термины
  getTerms: (params: any) => api.get('/api/v1/knowledge/entries', { params }),
  getTerm: (id: string) => api.get(`/api/v1/knowledge/entries/${id}`),
  searchTerms: (query: string) => api.post('/api/v1/knowledge/search', { q: query }),
  getSuggestions: (query: string) => api.get('/api/v1/text-analysis/suggest', { params: { q: query } }),

  // Избранное
  addFavorite: (termId: string) => api.post(`/api/v1/user-terms/favorites/${termId}`),
  removeFavorite: (termId: string) => api.delete(`/api/v1/user-terms/favorites/${termId}`),
  getFavorites: () => api.get('/api/v1/user-terms/favorites'),
  checkFavorite: (termId: string) => api.get(`/api/v1/user-terms/favorites/${termId}/check`),
  getFavoritesCount: () => api.get('/api/v1/user-terms/favorites/count'),

  // История просмотров
  addView: (termId: string) => api.post('/api/v1/user-terms/view_history', null, {params:{term_id:termId}}),
  getViewHistory: () => api.get('/api/v1/user-terms/view_history'),
  getMostViewed: () => api.get('/api/v1/user-terms/view_history/most_viewed'),
  getViewStats: () => api.get('/api/v1/user-terms/view_history/stats'),
  clearViewHistory: () => api.delete('/api/v1/user-terms/view_history'),

  // Общая статистика
  getUserStats: () => api.get('/api/v1/user-terms/stats'),

  // Викторины
  createQuiz: (category?: string, difficulty?: string, totalQuestions?: number) =>
    api.post('/api/v1/quizzes/sessions', {
      category,
      difficulty,
      total_questions: totalQuestions || 10,
    }),
  getQuizSession: (sessionId: string) => api.get(`/api/v1/quizzes/sessions/${sessionId}`),
  getUserQuizSessions: (limit?: number, offset?: number, status?: string) =>
    api.get('/api/v1/quizzes/sessions', { params: { limit, offset, status } }),
  updateQuizProgress: (sessionId: string, currentQuestion: number) =>
    api.put(`/api/v1/quizzes/sessions/${sessionId}/progress`, null, {params:{current_question:currentQuestion}}),
  completeQuiz: (sessionId: string) => api.post(`/api/v1/quizzes/sessions/${sessionId}/complete`),
  abandonQuiz: (sessionId: string) =>
    api.post(`/api/v1/quizzes/sessions/${sessionId}/abandon`),
  submitAnswer: (sessionId: string, answer: Partial<QuizAnswer>) =>
    api.post(`/api/v1/quizzes/sessions/${sessionId}/answers`, answer),
  getSessionAnswers: (sessionId: string) =>
    api.get(`/api/v1/quizzes/sessions/${sessionId}/answers`),
  getQuizStats: () => api.get('/api/v1/quizzes/stats'),
  getCategoryStats: () => api.get('/api/v1/quizzes/stats/categories'),
  getDifficultyStats: () => api.get('/api/v1/quizzes/stats/difficulty'),

  // Health checks
  healthCheck: () => api.get('/api/v1/user-terms/health'),
  quizHealthCheck: () => api.get('/api/v1/quizzes/health'),
};

// Вспомогательные функции
export const apiHelpers = {
  // Обработка ошибок API
  handleApiError: (error: any): string => {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'Произошла неизвестная ошибка';
  },

  // Проверка статуса ответа
  isSuccess: (status: number): boolean => {
    return status >= 200 && status < 300;
  },

  // Форматирование даты
  formatDate: (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  // Форматирование времени
  formatTime: (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  },

  // Получение цвета категории
  getCategoryColor: (category: string): string => {
    const colors: { [key: string]: string } = {
      tajweed: '#4caf50',
      fiqh: '#2196f3',
      aqeedah: '#ff9800',
      grammar: '#9c27b0',
      default: '#757575',
    };
    return colors[category] || colors.default;
  },

  // Получение иконки категории
  getCategoryIcon: (category: string): string => {
    const icons: { [key: string]: string } = {
      tajweed: '📖',
      fiqh: '⚖️',
      aqeedah: '🕌',
      grammar: '📝',
      default: '📚',
    };
    return icons[category] || icons.default;
  },
};

export default arabicTermsApi;
